<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Hero Section using shared component -->
    <UiPageHeroSection
      title="Épargne Omra Tayssir"
      breadcrumb="Épargne Omra"
      description="Préparez votre Omra sereinement avec notre plan d'épargne adapté à vos besoins et conforme aux principes islamiques"
      background-image="/images/hero-epargne-omra.webp"
      :breadcrumb-links="[{ label: 'épargne', to: '/epargne' }]"
      mobile-height="h-[402px]"
    >
      <template #badges>
        <!-- 100% Halal -->
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-6">
          <div
            class="flex items-center gap-2 px-0 sm:border-r sm:border-white/10"
          >
            <div class="text-primary">
              <IconHalal :size="32" />
            </div>
            <span class="text-white text-xl font-medium font-outfit">
              100% Halal
            </span>
          </div>

          <div
            class="flex items-center gap-2 px-0 sm:border-r sm:border-white/10"
          >
            <div class="text-primary">
              <IconMarge0 :size="32" />
            </div>
            <span class="text-white text-xl font-medium font-outfit">
              0% Marge
            </span>
          </div>

          <div class="flex items-center gap-2 col-span-2 sm:col-span-1">
            <div class="text-primary">
              <Icon name="heroicons:arrow-uturn-left" class="w-6 h-6" />
            </div>
            <span class="text-white text-xl font-medium font-outfit">
              Remboursable
            </span>
          </div>
        </div>
      </template>
    </UiPageHeroSection>

    <!-- Main Content -->
    <main class="bg-gray-50 py-10 sm:py-14 lg:py-16 mb-0 sm:mb-16">
      <div class="px-4 sm:px-6 lg:px-20">
        <div class="max-w-[1280px] mx-auto">
          <div class="flex flex-col-reverse lg:flex-row gap-6 lg:gap-8">
            <!-- Left Column - Content -->
            <div class="flex-1 space-y-6 max-w-full lg:max-w-[836px]">
              <!-- De quoi s'agit-il -->
              <section
                class="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6"
              >
                <div class="flex flex-col gap-3">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-10 h-10 bg-[#fff0d1] rounded-full flex items-center justify-center"
                    >
                      <IconQuestionLight :size="24" color="#C3993E" />
                    </div>
                    <h2
                      class="text-xl sm:text-2xl font-semibold text-gray-800 font-outfit"
                    >
                      De quoi s'agit-il ?
                    </h2>
                  </div>
                  <p
                    class="text-[15px] sm:text-lg text-gray-500 leading-[1.6] font-outfit"
                  >
                    Tayba Travel vous propose un produit d'épargne assorti, si
                    nécessaire, d'un financement sans marge de profit, pour vous
                    aider à accomplir votre pèlerinage dans de meilleures
                    conditions.
                  </p>
                </div>
                <div
                  class="mt-4 bg-[#eff6ff] border border-[#93c5fd] rounded-md px-2 py-2.5 flex items-center gap-1.5"
                >
                  <IconInfo :size="24" color="#70B0FF" class="flex-shrink-0" />
                  <p
                    class="text-[#1e40af] text-md font-medium font-outfit leading-[21px]"
                  >
                    L'Épargne Omra Tayssir est conforme aux prescriptions
                    islamiques.
                  </p>
                </div>
              </section>

              <!-- Pour qui -->
              <section
                class="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6"
              >
                <div class="flex flex-col gap-3">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-10 h-10 bg-[#fff0d1] rounded-full flex items-center justify-center"
                    >
                      <IconForWho :size="28" color="#D0A444" />
                    </div>
                    <h2
                      class="text-xl sm:text-2xl font-semibold text-gray-800 font-outfit"
                    >
                      Pour qui ?
                    </h2>
                  </div>
                  <ul class="space-y-2">
                    <li class="flex items-start gap-2 text-gray-600">
                      <Icon
                        name="heroicons:check-circle-solid"
                        class="w-[18px] h-[18px] text-primary-green flex-shrink-0"
                      />
                      <span
                        class="text-[15px] sm:text-lg leading-[1.4] font-outfit"
                        >Les salariés des entreprises privées et publiques ainsi
                        que les fonctionnaires</span
                      >
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <Icon
                        name="heroicons:check-circle-solid"
                        class="w-[18px] h-[18px] text-primary-green flex-shrink-0"
                      />
                      <span
                        class="text-[15px] sm:text-lg leading-[1.4] font-outfit"
                        >Les retraités et les bénéficiaires de pension</span
                      >
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <Icon
                        name="heroicons:check-circle-solid"
                        class="w-[18px] h-[18px] text-primary-green flex-shrink-0"
                      />
                      <span
                        class="text-[15px] sm:text-lg leading-[1.4] font-outfit"
                        >Les indépendants, les commerçants, les artisans et les
                        chefs d'entreprises</span
                      >
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <Icon
                        name="heroicons:check-circle-solid"
                        class="w-[18px] h-[18px] text-primary-green flex-shrink-0"
                      />
                      <span
                        class="text-[15px] sm:text-lg leading-[1.4] font-outfit"
                        >Les chômeurs, les étudiants et les personnes sans
                        revenus avec cosignataires</span
                      >
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <Icon
                        name="heroicons:check-circle-solid"
                        class="w-[18px] h-[18px] text-primary-green flex-shrink-0"
                      />
                      <span
                        class="text-[15px] sm:text-lg leading-[1.4] font-outfit"
                        >Les mineurs doivent être représentés par le tuteur
                        légal</span
                      >
                    </li>
                  </ul>
                </div>
              </section>

              <!-- Comment ça marche -->
              <section
                class="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6"
              >
                <div class="flex flex-col gap-6">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-10 h-10 bg-[#fff0d1] rounded-full flex items-center justify-center"
                    >
                      <IconHowItWorks :size="38" color="#D0A444" />
                    </div>
                    <h2
                      class="text-xl sm:text-2xl font-semibold text-gray-800 font-outfit"
                    >
                      Comment ça marche ?
                    </h2>
                  </div>
                  <ul class="space-y-3">
                    <li class="flex items-start gap-2 text-gray-600">
                      <div
                        class="w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                      >
                        1
                      </div>
                      <span
                        class="text-[15px] sm:text-lg leading-[1.5] font-outfit"
                        >Souscription à l'épargne pour une durée de 5, 4, 3, 2
                        ou 1 an(s)</span
                      >
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <div
                        class="w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-1"
                      >
                        2
                      </div>
                      <div class="font-outfit text-[15px] sm:text-lg">
                        <span>Versement mensuel à partir de 20 €, soit :</span>
                        <ul class="mt-2 ml-3 space-y-1 list-disc text-gray-600">
                          <li>
                            <span class="font-bold text-gray-800">20 €</span>
                            pour un contrat d'épargne de 5 ans
                          </li>
                          <li>
                            <span class="font-bold text-gray-800">25 €</span>
                            pour un contrat d'épargne de 4 ans
                          </li>
                          <li>
                            <span class="font-bold text-gray-800">35 €</span>
                            pour un contrat d'épargne de 3 ans
                          </li>
                          <li>
                            <span class="font-bold text-gray-800">50 €</span>
                            pour un contrat d'épargne de 2 ans
                          </li>
                          <li>
                            <span class="font-bold text-gray-800">100 €</span>
                            pour un contrat d'épargne de 1 an
                          </li>
                        </ul>
                      </div>
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <div
                        class="w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                      >
                        3
                      </div>
                      <span
                        class="text-[15px] sm:text-lg leading-[1.5] font-outfit"
                        >Un financement équivalent à 30% de l'épargne sans
                        dépasser 50% des frais de la Omra</span
                      >
                    </li>
                    <li class="flex items-start gap-2 text-gray-600">
                      <div
                        class="w-5 h-5 bg-primary text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-1"
                      >
                        4
                      </div>
                      <span
                        class="text-[15px] sm:text-lg leading-[1.5] font-outfit"
                        >Remboursement sur une année avec des mensualités fixes
                        durant toute la période de remboursement sans aucune
                        marge de profit et abandon de créance en cas de
                        décès</span
                      >
                    </li>
                  </ul>
                </div>
              </section>

              <!-- Avantages -->
              <section
                class="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6"
              >
                <div class="flex flex-col gap-6">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-10 h-10 bg-[#fff9ec] rounded-full flex items-center justify-center"
                    >
                      <IconStar :size="28" color="#D0A444" />
                    </div>
                    <h2
                      class="text-2xl sm:text-3xl font-semibold text-gray-800 font-outfit leading-6"
                    >
                      Avantages
                    </h2>
                  </div>
                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                    <div
                      class="bg-[#fff9ec] border border-[#ffcb58] rounded-xl px-4 py-8 sm:py-12 text-center flex flex-col items-center gap-2"
                    >
                      <div class="w-11 h-11 flex items-center justify-center">
                        <IconFacilite :size="26" color="#D0A444" />
                      </div>
                      <h4
                        class="font-semibold text-gray-800 text-[18px] tracking-[-0.18px] font-outfit leading-none"
                      >
                        Facilité
                      </h4>
                      <p
                        class="text-[15px] text-gray-500 font-outfit leading-[1.5]"
                      >
                        Facilité au niveau de la prise de décision
                      </p>
                    </div>
                    <div
                      class="bg-[#fff9ec] border border-[#ffcb58] rounded-xl px-4 py-12 text-center flex flex-col items-center gap-2"
                    >
                      <div class="w-11 h-11 flex items-center justify-center">
                        <IconMarge0 :size="32" color="#D0A444" />
                      </div>
                      <h4
                        class="font-semibold text-gray-800 text-[18px] tracking-[-0.18px] font-outfit leading-none"
                      >
                        0% Marge
                      </h4>
                      <p
                        class="text-[15px] text-gray-500 font-outfit leading-[1.5]"
                      >
                        Remboursement sans aucune marge de profit
                      </p>
                    </div>
                    <div
                      class="bg-[#fff9ec] border border-[#ffcb58] rounded-xl px-4 py-12 text-center flex flex-col items-center gap-2"
                    >
                      <div class="w-11 h-11 flex items-center justify-center">
                        <IconRemboursable :size="37" color="#D0A444" />
                      </div>
                      <h4
                        class="font-semibold text-gray-800 text-[18px] tracking-[-0.18px] font-outfit leading-none"
                      >
                        Remboursable
                      </h4>
                      <p
                        class="text-[15px] text-gray-500 font-outfit leading-[1.5]"
                      >
                        Facilité au niveau de la prise de décision
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Formulaire d'inscription -->
              <section
                id="formulaire"
                class="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6"
              >
                <div class="flex items-center gap-2 mb-4 sm:mb-6">
                  <div
                    class="w-9 h-9 sm:w-10 sm:h-10 bg-[#fff0d1] rounded-full flex items-center justify-center"
                  >
                    <IconDocumentInfo :size="22" color="#C3993E" />
                  </div>
                  <h2
                    class="text-[20px] sm:text-2xl lg:text-3xl font-semibold text-gray-800 font-outfit"
                  >
                    Formulaire d'inscription
                  </h2>
                </div>

                <form
                  @submit.prevent="handleSubmit"
                  :key="formKey"
                  class="space-y-4"
                >
                  <!-- Personal Info -->
                  <div class="my-5">
                    <label
                      class="text-md font-medium text-gray-700 leading-6 mb-2 block"
                    >
                      Genre <span class="text-red-600">*</span>
                    </label>
                    <div class="flex items-center gap-6 mt-2">
                      <RadioButton
                        v-model="form.genre"
                        value="masculin"
                        label="Masculin"
                      />
                      <RadioButton
                        v-model="form.genre"
                        value="feminin"
                        label="Féminin"
                      />
                    </div>
                    <span
                      v-if="errors.genre"
                      class="text-xs text-red-600 mt-1 block"
                      >{{ errors.genre }}</span
                    >
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <UiFormTextInput
                      v-model="form.nom"
                      label="Nom"
                      placeholder="Votre nom"
                      required
                      :error-message="errors.nom"
                      @blur="validateField('nom')"
                    />
                    <UiFormTextInput
                      v-model="form.prenom"
                      label="Prénom"
                      placeholder="Votre prénom"
                      required
                      :error-message="errors.prenom"
                      @blur="validateField('prenom')"
                    />
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <UiFormDatePicker
                      v-model="form.dateNaissance"
                      label="Date de naissance"
                      placeholder="Sélectionnez votre date de naissance"
                      :max-date="maxBirthDate"
                      required
                      :error="errors.dateNaissance"
                      @blur="validateField('dateNaissance')"
                    />
                    <UiNavigationDropdown
                      v-model="form.lieuNaissance"
                      :options="COUNTRIES_FR"
                      label="Lieu de naissance"
                      placeholder="Sélectionner un pays"
                      required
                      searchable
                      search-placeholder="Rechercher un pays..."
                      :error-message="errors.lieuNaissance"
                      @blur="validateField('lieuNaissance')"
                    />
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <UiFormTextInput
                      v-model="form.email"
                      label="E-mail"
                      type="email"
                      placeholder="votre@email.com"
                      required
                      :error-message="errors.email"
                      @blur="validateField('email')"
                    />
                    <div
                      class="flex flex-col gap-2 w-full"
                      @focusout="onPhoneFocusOut"
                    >
                      <label
                        class="text-md font-medium text-gray-700 leading-6"
                      >
                        Téléphone <span class="text-red-600">*</span>
                      </label>
                      <PhoneInput
                        v-model="form.telephone"
                        required
                        :error="errors.telephone"
                        @validity="onPhoneValidity"
                      />
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <UiFormTextInput
                      v-model="form.adresse"
                      label="Adresse"
                      placeholder="Votre adresse"
                      required
                      :error-message="errors.adresse"
                      @blur="validateField('adresse')"
                    />
                    <UiFormTextInput
                      v-model="form.codePostal"
                      label="Code postal"
                      type="number"
                      placeholder="Code postal"
                      required
                      :error-message="errors.codePostal"
                      @blur="validateField('codePostal')"
                    />
                    <UiFormTextInput
                      v-model="form.commune"
                      label="Commune"
                      placeholder="Votre commune"
                      required
                      :error-message="errors.commune"
                      @blur="validateField('commune')"
                    />
                  </div>

                  <!-- Subscription Options -->
                  <div>
                    <label
                      class="text-base font-medium text-gray-700 mb-2 mt-6 block"
                    >
                      Sur combien de temps souhaitez-vous épargner ?
                      <span class="text-red-600">*</span>
                    </label>
                    <div
                      class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
                    >
                      <div
                        v-for="option in subscriptionOptions"
                        :key="option.value"
                        class="p-4 border-2 rounded-lg cursor-pointer transition-all text-center"
                        :class="[
                          form.subscription === option.value
                            ? 'border-primary-green bg-primary-greenLight'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50',
                          errors.subscription && !form.subscription
                            ? 'border-red-300'
                            : '',
                        ]"
                        @click="form.subscription = option.value"
                      >
                        <div class="flex flex-col items-center gap-4">
                          <!-- Checkmark circle -->
                          <div class="w-5 h-5 flex items-center justify-center">
                            <svg
                              v-if="form.subscription === option.value"
                              class="w-5 h-5 text-primary-green"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                                clip-rule="evenodd"
                              />
                            </svg>
                            <div
                              v-else
                              class="w-5 h-5 rounded-full border-2 border-gray-300"
                            ></div>
                          </div>
                          <div class="flex flex-col items-center gap-1">
                            <div
                              class="text-2xl font-bold font-outfit text-gray-800"
                            >
                              {{ option.duration }}
                            </div>
                            <div
                              class="text-[15px] font-semibold font-outfit text-gray-600"
                            >
                              {{ option.price }}/mois
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span
                      v-if="errors.subscription"
                      class="text-xs text-red-600 mt-1 block"
                      >{{ errors.subscription }}</span
                    >
                  </div>

                  <!-- Terms -->
                  <div class="pt-2">
                    <Checkbox
                      v-model="form.acceptTerms"
                      align="start"
                      class="max-w-xl leading-4 mb-4"
                    >
                      <span class="text-base font-normal text-gray-600">
                        J'ai lu et approuvé les conditions générales au verso et
                        j'ai présenté tous les renseignements nécessaires à ma
                        souscription à l'épargne « Omra ».
                      </span>
                    </Checkbox>
                    <span
                      v-if="errors.acceptTerms"
                      class="text-xs text-red-600 mt-1 block"
                      >{{ errors.acceptTerms }}</span
                    >
                  </div>

                  <!-- Submit -->
                  <button
                    type="submit"
                    :disabled="isSubmitting"
                    class="w-full inline-flex items-center justify-center gap-2 text-lg bg-primary text-white px-8 py-3.5 rounded-full font-semibold hover:bg-primary-amber focus:ring-4 focus:ring-primary/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon
                      v-if="isSubmitting"
                      name="heroicons:arrow-path"
                      class="w-5 h-5 animate-spin"
                    />
                    <Icon
                      v-else
                      name="heroicons:paper-airplane"
                      class="w-5 h-5"
                    />
                    {{ isSubmitting ? "Envoi en cours..." : "Envoyer" }}
                  </button>

                  <div
                    v-if="submitSuccess"
                    class="bg-green-50 border-2 border-green-500 rounded-xl p-4 flex items-start gap-3 animate-fade-in mt-4"
                  >
                    <div
                      class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <Icon name="heroicons:check" class="w-6 h-6 text-white" />
                    </div>
                    <div class="flex-1">
                      <h4 class="text-green-900 font-semibold text-lg mb-1">
                        Votre demande a été envoyée avec succès !
                      </h4>
                      <p class="text-green-700 text-base">
                        Nous avons bien reçu votre demande et nous vous
                        répondrons dans les plus brefs délais.
                      </p>
                    </div>
                  </div>

                  <div
                    v-if="submitError"
                    class="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-start gap-3 mt-4"
                  >
                    <div
                      class="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0"
                    >
                      <Icon
                        name="heroicons:exclamation-triangle"
                        class="w-6 h-6 text-white"
                      />
                    </div>
                    <div class="flex-1">
                      <h4 class="text-red-900 font-semibold text-lg mb-1">
                        Erreur lors de l'envoi
                      </h4>
                      <p class="text-red-700 text-base">{{ submitError }}</p>
                    </div>
                  </div>
                </form>
              </section>
            </div>

            <!-- Right Column - Sidebar -->
            <div class="w-full lg:w-[412px] flex-shrink-0">
              <div class="flex flex-col gap-6 lg:sticky lg:top-24">
                <!-- Pricing Card -->
                <div
                  class="bg-white rounded-2xl border border-gray-200 px-5 py-6"
                >
                  <div class="flex flex-col gap-7">
                    <!-- Header with pricing -->
                    <div class="flex items-start justify-between">
                      <div class="flex flex-col gap-1">
                        <h3
                          class="text-xl font-semibold text-gray-800 font-outfit leading-8"
                        >
                          Épargne Omra Tayssir
                        </h3>
                        <p
                          class="text-base font-medium text-gray-600 font-outfit"
                        >
                          Versement minimum
                        </p>
                      </div>
                      <div class="flex flex-col items-end gap-1.5">
                        <div class="flex items-start gap-1">
                          <span class="text-xl font-medium text-gray-800"
                            >€</span
                          >
                          <span
                            class="text-[28px] font-black text-gray-900 tracking-[-0.98px] leading-none"
                            >20,00</span
                          >
                        </div>
                        <span
                          class="text-sm font-medium text-gray-500 text-right"
                          >/ par mois</span
                        >
                      </div>
                    </div>

                    <!-- Info rows -->
                    <div class="flex flex-col gap-5">
                      <!-- Durée -->
                      <div
                        class="flex items-center justify-between pb-5 border-b border-gray-300"
                      >
                        <div class="flex items-center gap-2">
                          <div class="w-7 h-7 p-0.5">
                            <Icon name="heroicons:clock" class="text-primary" />
                          </div>
                          <span
                            class="text-base font-medium text-gray-500 font-outfit"
                            >Durée</span
                          >
                        </div>
                        <span
                          class="text-lg font-bold text-gray-800 font-outfit tracking-[-0.36px]"
                          >1 à 5 ans</span
                        >
                      </div>
                      <!-- Financement max -->
                      <div
                        class="flex items-center justify-between pb-5 border-b border-gray-300"
                      >
                        <div class="flex items-center gap-2">
                          <div class="w-7 h-7 p-0.5 text-primary">
                            <IconEpargne :size="26" />
                          </div>
                          <span
                            class="text-base font-medium text-gray-500 font-outfit"
                            >Financement max</span
                          >
                        </div>
                        <span
                          class="text-lg font-bold text-gray-800 font-outfit tracking-[-0.36px]"
                          >30%</span
                        >
                      </div>
                      <!-- Marge de profit -->
                      <div class="flex items-center justify-between pb-5">
                        <div class="flex items-center gap-2">
                          <div class="text-primary">
                            <div class="w-7 h-7 p-0.5">
                              <IconMarge0 :size="24" />
                            </div>
                          </div>
                          <span
                            class="text-base font-medium text-gray-500 font-outfit"
                            >Marge de profit</span
                          >
                        </div>
                        <span
                          class="text-lg font-bold text-gray-800 font-outfit tracking-[-0.36px]"
                          >0%</span
                        >
                      </div>
                    </div>

                    <!-- Buttons -->
                    <div class="flex flex-col gap-3">
                      <a
                        href="#formulaire"
                        class="block w-full text-center bg-primary text-white px-4 py-4 rounded-[30px] font-semibold hover:bg-primary-darkAmber transition-colors font-outfit text-base shadow-sm"
                      >
                        S'inscrire maintenant
                      </a>

                      <NuxtLink
                        to="/contact"
                        class="block w-full text-center border border-primary text-primary px-4 py-4 rounded-[30px] font-semibold hover:bg-primary-light transition-colors font-outfit text-base shadow-sm"
                      >
                        Des questions ?
                      </NuxtLink>
                    </div>
                  </div>
                </div>

                <!-- WhatsApp Card -->
                <UiWhatsAppCard />
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- CTA Section -->
    <div class="w-full bg-white pt-10 md:pt-14">
      <UiCTASection />
    </div>

    <!-- Floating WhatsApp Button -->
    <WhatsAppFloatingButton />
    <MessengerFloatingButton />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from "vue";
import TextInput from "~/components/ui/form/TextInput.vue";
import PhoneInput from "~/components/ui/form/PhoneInput.vue";
import Checkbox from "~/components/ui/form/Checkbox.vue";
import RadioButton from "~/components/ui/form/RadioButton.vue";
import { useSnackbar } from "~/composables/ui/useSnackbar";
import { COUNTRIES_FR } from "~/utils/countries";
import { logger } from "~/utils/logger";
import IconHalal from "~/components/icons/IconHalal.vue";
import IconMarge0 from "~/components/icons/IconMarge0.vue";
import IconQuestionLight from "~/components/icons/IconQuestionLight.vue";
import IconForWho from "~/components/icons/IconForWho.vue";
import IconHowItWorks from "~/components/icons/IconHowItWorks.vue";
import IconFacilite from "~/components/icons/IconFacilite.vue";
import IconRemboursable from "~/components/icons/IconRemboursable.vue";
import IconEpargne from "~/components/icons/IconEpargne.vue";
import IconDocuments from "~/components/icons/IconDocuments.vue";
import IconDocumentInfo from "~/components/icons/IconDocumentInfo.vue";
import IconInfo from "~/components/icons/IconInfo.vue";
import IconStar from "~/components/icons/IconStar.vue";

import Icon from "~/components/Icon.vue";
import IconRefundable from "~/components/icons/IconRefundable.vue";
import IconRight from "~/components/icons/IconRight.vue";
import WhatsAppFloatingButton from "~/components/ui/WhatsAppFloatingButton.vue";
import MessengerFloatingButton from "~/components/ui/MessengerFloatingButton.vue";

// Page meta
definePageMeta({
  title: "Épargne Omra Tayssir",
  description:
    "Préparez votre Omra avec notre plan d'épargne Tayssir. Versements à partir de 20€/mois, financement sans marge de profit, conforme aux prescriptions islamiques.",
});

const config = useRuntimeConfig();
const siteUrl = ((config.public.siteUrl as string) || "https://taybatravel.be").replace(/\/$/, "");

useSeoMeta({
  title: "Épargne Omra Tayssir - Tayba Travel | Financement Islamique",
  description:
    "Partez en Omra sereinement. Notre plan d'épargne Tayssir : dès 20€/mois, sans intérêt, sans assurance vie, conforme aux prescriptions islamiques.",
  keywords:
    "épargne omra, tayssir, financement omra, islamique, halal, tayba travel, pèlerinage",
  ogTitle: "Épargne Omra Tayssir - Tayba Travel",
  ogDescription:
    "Préparez votre Omra dès aujourd'hui avec notre plan d'épargne islamique. 20€/mois sur 5 ans.",
  ogUrl: `${siteUrl}/epargne/epargne-omra`,
  ogImage: `${siteUrl}/pwa-512x512.png`,
  ogType: "website",
  twitterCard: "summary_large_image",
});
useHead({
  link: [{ rel: "canonical", href: `${siteUrl}/epargne/epargne-omra` }],
});

const snackbar = useSnackbar();

// Subscription options
const subscriptionOptions = [
  {
    label: "5 ans / 20€ par mois",
    value: "5ans",
    duration: "5 ans",
    price: "20€",
  },
  {
    label: "4 ans / 25€ par mois",
    value: "4ans",
    duration: "4 ans",
    price: "25€",
  },
  {
    label: "3 ans / 35€ par mois",
    value: "3ans",
    duration: "3 ans",
    price: "35€",
  },
  {
    label: "2 ans / 50€ par mois",
    value: "2ans",
    duration: "2 ans",
    price: "50€",
  },
  {
    label: "1 an / 100€ par mois",
    value: "1an",
    duration: "1 an",
    price: "100€",
  },
];

// Form state
const form = reactive({
  nom: "",
  prenom: "",
  genre: "",
  dateNaissance: "",
  lieuNaissance: "",
  adresse: "",
  codePostal: "",
  commune: "",
  telephone: null as { country: string; phone: string } | null,
  email: "",
  subscription: "",
  acceptTerms: false,
});

// Form errors
const errors = reactive({
  nom: "",
  prenom: "",
  genre: "",
  dateNaissance: "",
  lieuNaissance: "",
  adresse: "",
  codePostal: "",
  commune: "",
  telephone: "",
  email: "",
  subscription: "",
  acceptTerms: "",
});

const isSubmitting = ref(false);
const submitSuccess = ref(false);
const submitError = ref("");
const formKey = ref(0); // Key to force form re-render when needed
const phoneValid = ref(true);
const phoneTouched = ref(false);

// Max date for date picker (must be born)
const maxBirthDate = computed(() => {
  const today = new Date();
  return today;
});

// Reset errors
const resetErrors = () => {
  errors.nom = "";
  errors.prenom = "";
  errors.genre = "";
  errors.dateNaissance = "";
  errors.lieuNaissance = "";
  errors.adresse = "";
  errors.codePostal = "";
  errors.commune = "";
  errors.telephone = "";
  errors.email = "";
  errors.subscription = "";
  errors.acceptTerms = "";
};

const validateTelephone = () => {
  if (!form.telephone?.phone?.trim()) {
    errors.telephone = "Le téléphone est requis";
    return;
  }

  if (!phoneValid.value) {
    errors.telephone = "Le téléphone n'est pas valide";
    return;
  }

  errors.telephone = "";
};

const validateField = (field: keyof typeof errors) => {
  switch (field) {
    case "genre":
      errors.genre = form.genre ? "" : "Le genre est requis";
      break;
    case "nom":
      errors.nom = form.nom.trim() ? "" : "Le nom est requis";
      break;
    case "prenom":
      errors.prenom = form.prenom.trim() ? "" : "Le prénom est requis";
      break;
    case "dateNaissance":
      errors.dateNaissance = form.dateNaissance
        ? ""
        : "La date de naissance est requise";
      break;
    case "lieuNaissance":
      errors.lieuNaissance = form.lieuNaissance
        ? ""
        : "Le lieu de naissance est requis";
      break;
    case "adresse":
      errors.adresse = form.adresse.trim() ? "" : "L'adresse est requise";
      break;
    case "codePostal":
      if (!form.codePostal.trim()) {
        errors.codePostal = "Le code postal est requis";
        break;
      }
      errors.codePostal = /^\d+$/.test(form.codePostal.trim())
        ? ""
        : "Le code postal doit contenir uniquement des chiffres";
      break;
    case "commune":
      errors.commune = form.commune.trim() ? "" : "La commune est requise";
      break;
    case "telephone":
      validateTelephone();
      break;
    case "email":
      if (!form.email.trim()) {
        errors.email = "L'email est requis";
        break;
      }
      errors.email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())
        ? ""
        : "L'email n'est pas valide";
      break;
    case "subscription":
      errors.subscription = form.subscription
        ? ""
        : "Veuillez choisir une formule d'épargne";
      break;
    case "acceptTerms":
      errors.acceptTerms = form.acceptTerms
        ? ""
        : "Veuillez accepter les conditions générales";
      break;
    default:
      break;
  }
};

const onPhoneValidity = (isValid: boolean) => {
  phoneValid.value = isValid;
  if (phoneTouched.value) {
    validateTelephone();
  }
};

const onPhoneFocusOut = (event: FocusEvent) => {
  const currentTarget = event.currentTarget as HTMLElement | null;
  const nextTarget = event.relatedTarget as Node | null;
  if (currentTarget && nextTarget && currentTarget.contains(nextTarget)) {
    return;
  }
  phoneTouched.value = true;
  validateTelephone();
};

// Validate form
const validateForm = (): boolean => {
  let isValid = true;
  resetErrors();

  if (!form.genre) {
    errors.genre = "Le genre est requis";
    isValid = false;
  }

  if (!form.nom.trim()) {
    errors.nom = "Le nom est requis";
    isValid = false;
  }

  if (!form.prenom.trim()) {
    errors.prenom = "Le prénom est requis";
    isValid = false;
  }

  if (!form.dateNaissance) {
    errors.dateNaissance = "La date de naissance est requise";
    isValid = false;
  }

  if (!form.lieuNaissance) {
    errors.lieuNaissance = "Le lieu de naissance est requis";
    isValid = false;
  }

  if (!form.adresse.trim()) {
    errors.adresse = "L'adresse est requise";
    isValid = false;
  }

  if (!form.codePostal.trim()) {
    errors.codePostal = "Le code postal est requis";
    isValid = false;
  } else if (!/^\d+$/.test(form.codePostal.trim())) {
    errors.codePostal = "Le code postal doit contenir uniquement des chiffres";
    isValid = false;
  }

  if (!form.commune.trim()) {
    errors.commune = "La commune est requise";
    isValid = false;
  }

  if (!form.telephone?.phone?.trim()) {
    errors.telephone = "Le téléphone est requis";
    isValid = false;
  }

  if (!form.email.trim()) {
    errors.email = "L'email est requis";
    isValid = false;
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "L'email n'est pas valide";
    isValid = false;
  }

  if (!form.subscription) {
    errors.subscription = "Veuillez choisir une formule d'épargne";
    isValid = false;
  }

  if (!form.acceptTerms) {
    errors.acceptTerms = "Veuillez accepter les conditions générales";
    isValid = false;
  }

  return isValid;
};

// Handle form submission
const handleSubmit = async () => {
  if (!validateForm()) {
    snackbar.show("Veuillez remplir tous les champs obligatoires", {
      variant: "error",
    });
    return;
  }

  isSubmitting.value = true;
  submitSuccess.value = false;

  try {
    await $fetch("/api/forms/epargne-omra", {
      method: "POST",
      body: {
        nom: form.nom,
        prenom: form.prenom,
        genre: form.genre,
        dateNaissance: form.dateNaissance,
        lieuNaissance: form.lieuNaissance,
        adresse: form.adresse,
        codePostal: form.codePostal,
        commune: form.commune,
        telephone: form.telephone
          ? `${form.telephone.country} ${form.telephone.phone}`
          : "",
        email: form.email,
        subscription: form.subscription,
      },
    });

    submitSuccess.value = true;
    snackbar.show("Votre demande a été envoyée avec succès !", {
      variant: "success",
    });

    // Reset form fields
    Object.assign(form, {
      nom: "",
      prenom: "",
      genre: "",
      dateNaissance: "",
      lieuNaissance: "",
      adresse: "",
      codePostal: "",
      commune: "",
      telephone: null,
      email: "",
      subscription: "",
      acceptTerms: false,
    });
    resetErrors();

    // Force re-render of form components to ensure all fields clear properly
    formKey.value++;

    // Scroll to success message
    const formElement = document.getElementById("formulaire");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    // Hide success message after 5 seconds
    setTimeout(() => {
      submitSuccess.value = false;
    }, 5000);
  } catch (error: any) {
    logger.error("Error submitting form:", error);
    submitError.value =
      error.data?.message || "Une erreur est survenue. Veuillez réessayer.";
    snackbar.show(submitError.value, {
      variant: "error",
    });
  } finally {
    isSubmitting.value = false;
  }
};
</script>
